/**
 * @name: Klaviyo BIS Reinit
 * @dependencies: klaviyo-onsite.js, snippets/klaviyo-scripts.liquid (window.__klaviyoBisConfig),
 *                assets/pdp-colorway-swap.js (dispatches pdp:swapped)
 * @strict-mode: true
 *
 * =============================================================================
 * How Klaviyo Back In Stock (BIS) works on first page load
 * =============================================================================
 *
 * 1. layout/theme.liquid loads assets/klaviyo-onsite.js (Klaviyo Backbone widget).
 * 2. snippets/klaviyo-scripts.liquid calls:
 *      klaviyo.init({ account, list, platform: 'shopify' })
 *      klaviyo.enable('backinstock', { trigger, modal })
 * 3. Klaviyo finds buttons with class `.klaviyo-bis-trigger` (Notify Me).
 * 4. It fetches the current product JSON once and caches variants in memory.
 * 5. It creates a single iframe, appends it to document.body, and sets:
 *      id="klaviyo-bis-iframe"
 *    The modal HTML (title, email field, submit) lives INSIDE that iframe.
 *    The form is NOT rendered by Liquid — only the trigger button is.
 *
 * Clicking Notify Me shows that iframe. Klaviyo has no public refresh() API.
 *
 * =============================================================================
 * Why Notify Me breaks after a colorway switch
 * =============================================================================
 *
 * Colorways are separate products. assets/pdp-colorway-swap.js swaps the PDP
 * via the Section Rendering API and updates the URL with history.pushState —
 * no full page reload.
 *
 * After the swap:
 *   - New `.klaviyo-bis-trigger` buttons exist in the replaced DOM.
 *   - The old #klaviyo-bis-iframe and in-memory product/variant cache remain
 *     bound to the PREVIOUS product.
 *   - Opening Notify Me fails or targets the wrong variant because
 *     variants_by_id no longer matches the new product.
 *
 * Judge.me / Swym already reinit on pdp:swapped; Klaviyo did not — this file
 * fills that gap.
 *
 * =============================================================================
 * How this file fixes it
 * =============================================================================
 *
 * Listens for `pdp:swapped` (fired by pdp-colorway-swap.js after sections update).
 *
 * Then runs reinitBackInStock():
 *   1. teardownBis()
 *        - Removes #klaviyo-bis-iframe and .klaviyo-bis-close
 *        - Removes existing klaviyo-onsite <script> tags
 *        - Deletes window.klaviyoBIS and window.klaviyo so enable() is not a no-op
 *        - Keeps window._klOnsite so other Klaviyo forms stay queued
 *   2. loadOnsiteScript()
 *        - Injects klaviyo-onsite.js again (cache-busted) into <head>
 *        - Resolves on script.onload (no timers)
 *   3. enableBackInStock()
 *        - Re-runs init + enable('backinstock') using window.__klaviyoBisConfig
 *          (account RtWLTU, list, modal copy — set in klaviyo-scripts.liquid)
 *        - Klaviyo re-reads the current product from the page/URL, rebuilds
 *          #klaviyo-bis-iframe, and rebinds .klaviyo-bis-trigger clicks
 *
 * Concurrent swaps are ignored while reinitting is true.
 *
 * While reinitting, Notify Me clicks are blocked. If the shopper clicks before
 * Klaviyo finishes loading, the iframe overlay can appear (and lock scroll)
 * before the form HTML is written inside it. Once enable() completes, clicks
 * work normally again.
 *
 * Note: We reload this theme's klaviyo-onsite.js (legacy BIS iframe), not the
 * newer static.klaviyo.com/.../klaviyo.js forms SDK — that URL does not drive
 * this store's Notify Me modal.
 */

(function () {
  const CONFIG = window.__klaviyoBisConfig;
  if (!CONFIG || !CONFIG.onsiteSrc) return;

  let reinitting = false;

  /**
   * Blocks Notify Me until BIS has finished enable(), so an empty iframe
   * overlay cannot lock page scroll mid-load.
   * @param {boolean} ready
   */
  const setBisReady = (ready) => {
    document.documentElement.toggleAttribute('data-klaviyo-bis-ready', ready);
  };

  /**
   * @param {Event} event
   */
  const blockNotifyWhileLoading = (event) => {
    if (!reinitting) return;

    const trigger = event.target instanceof Element
      ? event.target.closest('.klaviyo-bis-trigger')
      : null;

    if (!trigger) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  document.addEventListener('click', blockNotifyWhileLoading, true);
  setBisReady(true);

  /**
   * Removes the stale BIS iframe and Backbone instance so enable() can run again.
   */
  const teardownBis = () => {
    const iframe = document.getElementById('klaviyo-bis-iframe');
    if (iframe) iframe.remove();

    document.querySelectorAll('.klaviyo-bis-close').forEach((el) => el.remove());

    document.querySelectorAll('script[data-klaviyo-onsite], script[src*="klaviyo-onsite"]').forEach((el) => {
      el.remove();
    });

    try {
      delete window.klaviyoBIS;
    } catch (_error) {
      window.klaviyoBIS = undefined;
    }

    // onsite.js recreates the klaviyo proxy; keep _klOnsite so forms stay queued
    try {
      delete window.klaviyo;
    } catch (_error) {
      window.klaviyo = undefined;
    }
  };

  /**
   * @param {string} src
   * @returns {Promise<void>}
   */
  const loadOnsiteScript = (src) => {
    return new Promise((resolve, reject) => {
      const separator = src.includes('?') ? '&' : '?';
      const script = document.createElement('script');
      script.src = `${src}${separator}bisReinit=${Date.now()}`;
      script.async = false;
      script.dataset.klaviyoOnsite = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to reload klaviyo-onsite.js'));
      document.head.appendChild(script);
    });
  };

  const enableBackInStock = () => {
    window.klaviyo = window.klaviyo || [];
    window.klaviyo.init({
      account: CONFIG.account,
      list: CONFIG.list,
      platform: 'shopify',
    });
    window.klaviyo.enable('backinstock', CONFIG.enableOptions);
  };

  /**
   * Full BIS teardown + onsite reload + re-enable for the current product DOM.
   * @returns {Promise<void>}
   */
  const reinitBackInStock = async () => {
    if (reinitting) return;
    reinitting = true;
    setBisReady(false);

    try {
      teardownBis();
      await loadOnsiteScript(CONFIG.onsiteSrc);
      enableBackInStock();
    } catch (error) {
      console.error('[klaviyo-bis-reinit]', error);
      teardownBis();
    } finally {
      reinitting = false;
      setBisReady(true);
    }
  };

  document.addEventListener('pdp:swapped', () => {
    reinitBackInStock();
  });
})();
